// AI-grading runs state — ONE tab-wide source for the caller's runs, filled
// by useAIGradingRuns from the status wire. The panel footer, the poll gate,
// and the EU row spinner-guard all read here.
import { create } from 'zustand';

/** RunJson — verbatim keys from the status wire (AiGradingRun#status_json). */
export interface AIGradingRunJson {
  run_id: string;
  status: string;
  done: number;
  total: number;
  no_guidance_count: number;
  illegible_count: number;
  no_material_skipped_count: number;
  seeds_sent: { questions: number; overall: number };
  only_ungraded: boolean | null;
  use_seeds: boolean | null;
  grading_basis: string | null;
  segment_ids: Array<string | number> | null;
  experience_session_ids: Array<string | number> | null;
  grade_overall: boolean;
  covers_experience: boolean;
  /** True when the run was started from a hand-picked submission set. An
   *  older backend omits it — absent reads as the whole pool. */
  hand_picked_roster?: boolean;
  /** Present only with hand_picked_roster — the run's picked submissions. */
  experience_user_ids?: Array<string | number>;
  started_by: { id: number; name: string | null };
  own: boolean;
  can_cancel: boolean;
  started_at: string | null;
}

export interface AIGradingAccess {
  org_id: number;
  /** null = whole experience (non-sessions); else the caller's grading scope. */
  experience_session_ids: Array<string | number> | null;
}

export interface AIGradingRunsResult {
  canGrade: boolean;
  reason: string | null;
  access: AIGradingAccess | null;
  activeRuns: AIGradingRunJson[];
  lastRun: AIGradingRunJson | null;
}

interface AIGradingRunsState extends Omit<AIGradingRunsResult, 'canGrade'> {
  /** The experience these runs belong to — see bindExperience. */
  experienceId: string | number | null;
  /** True after the first successful status fetch. */
  loaded: boolean;
  /** null until the first status fetch lands. */
  canGrade: boolean | null;
  bindExperience: (experienceId: string | number) => void;
  setRunsResult: (result: AIGradingRunsResult) => void;
  /** Adds the start response's run row — the poll confirms it next tick. */
  addStartedRun: (run: AIGradingRunJson) => void;
  /** Server-confirmed terminal transition (cancel POST 200). */
  finishRun: (runId: string, status: string) => void;
  /** Count, not a flag — the grading modal mounts one panel per response. */
  responseGradesInProgress: number;
  startResponseGrade: () => void;
  finishResponseGrade: () => void;
}

const initialRunsState = {
  loaded: false,
  canGrade: null as boolean | null,
  reason: null,
  access: null,
  activeRuns: [] as AIGradingRunJson[],
  lastRun: null,
};

export const useAIGradingRunsStore = create<AIGradingRunsState>((set) => ({
  experienceId: null,
  ...initialRunsState,
  // Outside initialRunsState — a rebind must not zero a live request.
  responseGradesInProgress: 0,
  // Runs are per-experience — rebinding to another experience resets them.
  bindExperience: (experienceId) =>
    set((state) =>
      String(state.experienceId) === String(experienceId)
        ? {}
        : { experienceId, ...initialRunsState }
    ),
  // Poll ticks re-send value-identical results — skip to spare re-renders.
  setRunsResult: (result) =>
    set((state) => {
      const previous: AIGradingRunsResult = {
        canGrade: state.canGrade as boolean,
        reason: state.reason,
        access: state.access,
        activeRuns: state.activeRuns,
        lastRun: state.lastRun,
      };
      if (state.loaded && JSON.stringify(previous) === JSON.stringify(result)) return {};
      return { loaded: true, ...result };
    }),
  addStartedRun: (run) =>
    set((state) => ({
      activeRuns: [...state.activeRuns.filter((entry) => entry.run_id !== run.run_id), run],
    })),
  finishRun: (runId, status) =>
    set((state) => {
      const run = state.activeRuns.find((entry) => entry.run_id === runId);
      if (!run) return {};
      return {
        activeRuns: state.activeRuns.filter((entry) => entry.run_id !== runId),
        lastRun: { ...run, status },
      };
    }),
  startResponseGrade: () =>
    set((state) => ({ responseGradesInProgress: state.responseGradesInProgress + 1 })),
  finishResponseGrade: () =>
    set((state) => ({
      responseGradesInProgress: Math.max(0, state.responseGradesInProgress - 1),
    })),
}));

export const selectAIGradingActiveRuns = (state: AIGradingRunsState) => state.activeRuns;
export const selectAIGradingLastRun = (state: AIGradingRunsState) => state.lastRun;
export const selectAIGradingCanGrade = (state: AIGradingRunsState) => state.canGrade;
export const selectAIGradingReason = (state: AIGradingRunsState) => state.reason;
export const selectAIGradingAccess = (state: AIGradingRunsState) => state.access;
export const selectAIGradingResponseGradeRunning = (state: AIGradingRunsState) =>
  state.responseGradesInProgress > 0;

/** Any ACTIVE run covering the session — frozen rosters make an unstamped row
 *  outside every covering run. A null session (legacy EU without one) is
 *  reachable by any run's roster, so any active run covers it. */
export const activeAIGradingRunForSession = (
  activeRuns: AIGradingRunJson[],
  sessionId?: string | number | null
): AIGradingRunJson | null => {
  if (sessionId == null) return activeRuns[0] ?? null;
  return (
    activeRuns.find(
      (run) =>
        run.covers_experience ||
        (run.experience_session_ids ?? []).some((id) => String(id) === String(sessionId))
    ) ?? null
  );
};
